import { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
// import CloseIcon from '@material-ui/icons/Close';
import HeightIcon from '@material-ui/icons/Height';
import Tooltip from '@material-ui/core/Tooltip';
import { fromOADate, addDays } from '../../../helpers/datetime';
import { toggleArrayItem } from '../../../helpers/state';
import LineChartNivo from './LineChartNivo';
import { useGetTextParameter } from '../../../helpers/hooks';
import { kassimGreen, kassimBlue, makeTransparent } from '../../../style/theme';

const InnerContainer = styled.div`
    display: flex;
    height: 100%;
    position: relative;
`;

const ChartButton = styled.div`
    position: absolute;
    top: 0px;
    right: 4px;
    cursor: pointer;
    z-index: 100;
    border: 1px solid ${makeTransparent(kassimGreen, 0.8)};
    border-radius: 4px;
    color: #1d5f1d;

    &:hover {
        background-color: ${makeTransparent(kassimGreen, 0.2)};
        border-color: ${makeTransparent(kassimGreen, 0.4)};
    }

    & svg {
        display: block;
        font-size: 28px;
    }
`;

const ZoomButton = styled(ChartButton)``;
const NormaliseButton = styled(ChartButton)`
    ${({ selected }) =>
        selected && `background-color: ${makeTransparent(kassimGreen, 0.4)}`};
`;

const OpenPopupIcon = styled(HeightIcon)`
    & path {
        transform-origin: center center;
        transform: rotate(-45deg);
    }

    &:nth-child(2) path {
        transform: rotate(45deg);
    }

    &:nth-child(2) {
        position: absolute;
        top: 0;
        left: 0;
    }
`;
const OpenPopupButton = styled(ChartButton)`
    left: 0px;
    right: auto;

    & svg {
        font-size: 20px;
    }
`;

const ChartContainer = styled.div`
    height: ${({ $height }) => $height};
    flex: 1;
    position: relative;
    min-width: 0;
`;

const ParameterContainer = styled.div`
    max-height: 100%;
    overflow-y: auto;
    margin-top: 8px;
`;

const StyledList = styled(List)`
    padding-top: 0;
`;
const StyledListItem = styled(ListItem)`
    padding: 4px;
    white-space: nowrap;
    font-weight: ${(props) => (props.selected ? 'bold' : 'normal')};

    &.Mui-selected,
    &.Mui-selected:hover {
        background: ${kassimBlue};
        color: white;
    }
`;
const StyledListItemText = styled(ListItemText)`
    font-size: 0.7rem;
    margin: 0;

    @media (max-width: 1200px) {
        font-size: 0.6rem;
    }
`;

const ParameterSelection = ({
    parameters,
    selectedParameter,
    setSelectedParameter,
}) => {
    const getTextParameter = useGetTextParameter();
    return (
        <StyledList>
            {parameters.map((param) => (
                <StyledListItem
                    key={param.key}
                    button
                    onClick={() => setSelectedParameter(param)}
                    selected={
                        selectedParameter &&
                        selectedParameter.length &&
                        selectedParameter.some((p) => p.key === param.key)
                    }
                    dense
                >
                    <StyledListItemText
                        disableTypography
                        primary={`${getTextParameter(
                            param.text
                        )} (${getTextParameter(param.unit)})`}
                    />
                </StyledListItem>
            ))}
        </StyledList>
    );
};

const OutputPanel = ({
    data,
    previousData,
    game,
    gameConfig,
    gameStatus,
    outputType = 'histoutput',
    chartHeight = '200px',
    openPopup,
    title,
}) => {
    const { t } = useTranslation();
    const getTextParameter = useGetTextParameter();
    const [selectedParameter, setSelectedParameter] = useState();
    const [zoomedToCurrentPeriod, setZoomedToCurrentPeriod] = useState(false);
    const [normaliseValues, setNormaliseValues] = useState(false);
    const allowMultiSelectParameter = outputType === 'lastoutput';

    const outputParameters = useMemo(
        () =>
            Object.entries(gameConfig)
                .filter(([key, value]) => {
                    return (
                        value.type === outputType &&
                        typeof game[key] !== 'undefined'
                    );
                })
                .map(([key, value]) => {
                    return { ...value, key };
                }),
        [gameConfig, game, outputType]
    );

    useEffect(() => {
        setSelectedParameter([outputParameters.find(() => true)]);
    }, [outputParameters]);

    const handleClickParameter = (param) => {
        if (allowMultiSelectParameter) {
            setSelectedParameter((prevSelection) => {
                return toggleArrayItem(
                    prevSelection || [],
                    param,
                    (p) => p.key
                );
            });
        } else {
            setSelectedParameter([param]);
        }
    };
    if (!data) {
        return null;
    }

    let nivoData = [];
    const chartProps = {
        enableArea: false,
        yScaleMin: 'auto',
        normalise: false,
    };
    let showZoomButton = false;
    let showNormaliseButton = false;
    if (outputType === 'histoutput') {
        const chartData =
            data && selectedParameter && selectedParameter.length
                ? data.DateTime?.data?.map((date, i) => {
                      return {
                          y: data[selectedParameter[0].key]?.data[i],
                          //   x: format(fromOADate(date), 'yyyy-MM-dd HH:mm:ss'),
                          x: fromOADate(date),
                      };
                  })
                : [];
        const gameStart = fromOADate(gameStatus.simBeg);

        chartProps.dateTicks = Array.from(
            { length: gameStatus.steps + 1 },
            (v, i) => addDays(gameStart, i * gameStatus.stepsize)
        );
        chartProps.xScaleMax = chartProps.dateTicks[gameStatus.steps];

        const pastPeriods = chartData.filter(
            (d) => d.x <= fromOADate(gameStatus.stepBeg)
        );
        const currentPeriod = chartData.filter(
            (d) => d.x >= fromOADate(gameStatus.stepBeg)
        );

        showZoomButton = currentPeriod.length > 0;
        nivoData = [];
        let mainData = chartData;

        if (zoomedToCurrentPeriod) {
            if (!showZoomButton) {
                setZoomedToCurrentPeriod(false);
            } else {
                mainData = currentPeriod;
                chartProps.xScaleMax = fromOADate(gameStatus.stepEnd);
                if (pastPeriods.length) {
                    mainData.unshift(pastPeriods[pastPeriods.length - 1]);
                    // center period with some padding left/right with the last previous datapoint
                    if (mainData.length >= 2) {
                        chartProps.xScaleMax = new Date(
                            chartProps.xScaleMax.getTime() +
                                Math.abs(
                                    mainData[1].x.getTime() -
                                        mainData[0].x.getTime()
                                ) /
                                    2
                        );
                    }
                }
            }
        }

        nivoData.push({
            id: getTextParameter(selectedParameter?.[0]?.text),
            color: 'rgb(31, 119, 180)',
            data: mainData,
        });

        if (previousData) {
            const previousChartData =
                selectedParameter && selectedParameter.length
                    ? previousData.DateTime?.data
                          ?.map((date, i) => {
                              return {
                                  y: previousData[selectedParameter[0].key]
                                      ?.data[i],
                                  //   x: format(fromOADate(date), 'yyyy-MM-dd HH:mm:ss'),
                                  x: fromOADate(date),
                              };
                          })
                          .filter((d) => d.x >= fromOADate(gameStatus.stepBeg))
                    : [];
            if (pastPeriods.length) {
                previousChartData.unshift(pastPeriods[pastPeriods.length - 1]);
            }
            nivoData.unshift({
                id: 'vorige poging',
                color: 'rgb(255, 127, 14)',
                lineStyle: {
                    strokeDasharray: '6, 1',
                    // strokeDashoffset: '2',
                    // strokeWidth: 1,
                },
                data: previousChartData,
            });
        }

        nivoData.unshift({
            id: 'huidige periode',
            lineStyle: {
                stroke: 'none',
            },
            background: kassimGreen + '40',
            color: kassimGreen + '40',
            data: [
                {
                    x: fromOADate(gameStatus.stepBeg),
                    y: mainData?.[0]?.y || 0,
                },
                {
                    x: fromOADate(gameStatus.stepEnd),
                    y: mainData?.[0]?.y || 0,
                },
            ],
        });
    } else {
        // outputType === 'lastoutput'
        // chartProps.yScaleMin = 'auto';
        if (data && selectedParameter && selectedParameter.length) {
            nivoData = selectedParameter.map((paramSelection) => {
                return {
                    id: getTextParameter(paramSelection.text),
                    data: data.DateTime?.data?.map((date, i) => {
                        return {
                            y: data[paramSelection.key]?.data[i],
                            x: fromOADate(date),
                        };
                    }),
                };
            });
            if (data.DateTime?.data?.[0]) {
                chartProps.xScaleMin = fromOADate(
                    data.DateTime.data[0]
                ).setHours(0, 0, 0, 0);
            }
            // chartProps.enableArea = selectedParameter.length === 1;
            showNormaliseButton = selectedParameter.length > 1;
            chartProps.normalise = normaliseValues;
        } else {
            nivoData = [];
        }
    }

    return (
        <InnerContainer>
            <ChartContainer $height={chartHeight}>
                {showZoomButton && (
                    <ZoomButton
                        onClick={() => {
                            setZoomedToCurrentPeriod((prev) => !prev);
                        }}
                    >
                        {zoomedToCurrentPeriod ? (
                            <ZoomOutIcon />
                        ) : (
                            <ZoomInIcon />
                        )}
                    </ZoomButton>
                )}
                {showNormaliseButton && (
                    <Tooltip title={t('charts.normalize')}>
                        <NormaliseButton
                            onClick={() => {
                                setNormaliseValues((prev) => !prev);
                            }}
                            selected={normaliseValues}
                        >
                            <HeightIcon />
                        </NormaliseButton>
                    </Tooltip>
                )}
                <LineChartNivo data={nivoData} title={title} {...chartProps} />
            </ChartContainer>
            <ParameterContainer>
                <ParameterSelection
                    parameters={outputParameters}
                    selectedParameter={selectedParameter}
                    setSelectedParameter={handleClickParameter}
                />
            </ParameterContainer>

            {openPopup && (
                <OpenPopupButton onClick={openPopup}>
                    <OpenPopupIcon />
                    <OpenPopupIcon />
                </OpenPopupButton>
            )}
        </InnerContainer>
    );
};

export default OutputPanel;
